import { useCallback } from "react";
import {
  listAdminUsers,
  createAdminUser,
  updateUserRoles,
  deactivateUser,
  reactivateUser,
  createExpertProfile,
  updateExpertFields,
  sendPasswordReset,
  type AdminUser,
  type CreateAdminUserParams,
  type AppRole,
} from "../../../../lib/adminApi";
import { useAsyncData } from "../../../../hooks/useAsyncData";
import { useUserContext } from "../../../../context/UserContext";

export type { AdminUser };

export interface UseAdminUsersResult {
  users: AdminUser[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  handleCreate: (params: CreateAdminUserParams) => Promise<void>;
  handleUpdateRoles: (userId: string, roles: AppRole[]) => Promise<void>;
  handleDeactivate: (userId: string) => Promise<void>;
  handleReactivate: (userId: string) => Promise<void>;
  handleCreateExpertProfile: (userId: string) => Promise<void>;
  handleUpdateExpertFields: (userId: string, fields: string[]) => Promise<void>;
  handleSendPasswordReset: (userId: string) => Promise<void>;
}

/** Loads the user list and exposes mutation helpers that auto-refresh on success. */
export function useAdminUsers(): UseAdminUsersResult {
  const { data, loading, error, refresh } = useAsyncData(listAdminUsers);
  const { session, refreshProfile } = useUserContext();
  const currentUserId = session?.user?.id ?? null;

  const handleCreate = useCallback(
    async (params: CreateAdminUserParams) => {
      await createAdminUser(params);
      await refresh();
    },
    [refresh]
  );

  const handleUpdateRoles = useCallback(
    async (userId: string, roles: AppRole[]) => {
      await updateUserRoles(userId, roles);
      await refresh();
      if (userId === currentUserId) await refreshProfile();
    },
    [refresh, currentUserId, refreshProfile]
  );

  const handleDeactivate = useCallback(
    async (userId: string) => {
      await deactivateUser(userId);
      await refresh();
    },
    [refresh]
  );

  const handleReactivate = useCallback(
    async (userId: string) => {
      await reactivateUser(userId);
      await refresh();
    },
    [refresh]
  );

  const handleCreateExpertProfile = useCallback(
    async (userId: string) => {
      await createExpertProfile(userId);
      await refresh();
      if (userId === currentUserId) await refreshProfile();
    },
    [refresh, currentUserId, refreshProfile]
  );

  const handleUpdateExpertFields = useCallback(
    async (userId: string, fields: string[]) => {
      await updateExpertFields(userId, fields);
      await refresh();
      if (userId === currentUserId) await refreshProfile();
    },
    [refresh, currentUserId, refreshProfile]
  );

  const handleSendPasswordReset = useCallback(async (userId: string) => {
    await sendPasswordReset(userId);
  }, []);

  return {
    users: data ?? [],
    loading,
    error,
    refresh,
    handleCreate,
    handleUpdateRoles,
    handleDeactivate,
    handleReactivate,
    handleCreateExpertProfile,
    handleUpdateExpertFields,
    handleSendPasswordReset,
  };
}
